import React, { useState } from "react";
import axios from "axios";
import { useLanguage } from "../i18n/LanguageContext";
import { API, IMG } from "../lib/constants";
import TrustStrip from "../components/TrustStrip";
import { Check, Loader2, DollarSign, Home as HomeIcon } from "lucide-react";
import SEOHead from "../components/SEOHead";

const Training = () => {
    const { t, lang } = useLanguage();
    const r = t.training;
    const [form, setForm] = useState({
        name: "", email: "", phone: "", instagram: "",
        background: "", goals: "", portfolio_link: "", start_timeframe: "",
    });
    const [status, setStatus] = useState({ submitting: false, success: false, error: "" });

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus({ submitting: true, success: false, error: "" });
        try {
            await axios.post(`${API}/training-inquiries`, { ...form, language: lang });
            setStatus({ submitting: false, success: true, error: "" });
            setForm({ name: "", email: "", phone: "", instagram: "", background: "", goals: "", portfolio_link: "", start_timeframe: "" });
        } catch (err) {
            setStatus({ submitting: false, success: false, error: t.common.errorSubmit });
        }
    };

    return (
        <>
            <SEOHead page="training" />
            {/* Hero */}
            <section
                data-testid="training-hero"
                className="relative min-h-[60vh] flex items-end overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img src={IMG.wendyOvertop} alt="Permanent makeup training and apprenticeship Las Vegas with Wendy Chester" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#3D3D24] via-[#3D3D24]/65 to-[#3D3D24]/20" />
                </div>
                <div className="relative w-full max-w-[1400px] mx-auto px-6 pb-14 md:pb-20 z-10">
                    <div className="max-w-3xl">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{r.heroOverline}</div>
                        <h1 className="font-serif text-[#FFFFF3] text-5xl md:text-7xl leading-[0.98] mb-6">
                            {r.heroTitle}
                        </h1>
                        <p className="text-[#FFFFF3]/90 text-base md:text-lg max-w-2xl leading-relaxed">
                            {r.heroSub}
                        </p>
                    </div>
                </div>
            </section>

            <TrustStrip variant="dark" />

            {/* Details */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-20">
                        {r.details.map((d, i) => (
                            <div
                                key={i}
                                data-testid={`training-detail-${i}`}
                                className="bg-[#3D3D24] text-[#FFFFF3] p-8"
                            >
                                <div className="font-serif text-3xl text-[#D4A95A] mb-3">0{i + 1}</div>
                                <div className="font-label text-[11px] mb-3">{d.label}</div>
                                <p className="text-sm opacity-80 leading-relaxed">{d.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* After graduation */}
                    <div data-testid="training-after" className="mb-20">
                        <div className="text-center mb-12">
                            <div className="font-label text-[11px] text-[#D4A95A] mb-5">After Graduation</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] max-w-3xl mx-auto">
                                {r.afterTitle}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-[1100px] mx-auto">
                            {r.afterCards.map((c, i) => (
                                <div
                                    key={i}
                                    data-testid={`training-after-card-${i}`}
                                    className="border border-[#3D3D24]/15 p-8 md:p-10 bg-[#F5F2E4] hover:border-[#3D3D24] transition-colors"
                                >
                                    <div className="w-12 h-12 bg-[#D4A95A] text-[#3D3D24] flex items-center justify-center mb-5">
                                        {i === 0 ? <HomeIcon size={20} /> : <Check size={20} strokeWidth={3} />}
                                    </div>
                                    <h3 className="font-serif text-2xl md:text-3xl text-[#3D3D24] mb-4 leading-tight">
                                        {c.title}
                                    </h3>
                                    <p className="text-[#3D3D24]/85 leading-relaxed">{c.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Training Studio Gallery */}
                    <div data-testid="training-gallery" className="mb-20">
                        <div className="text-center mb-12">
                            <div className="font-label text-[11px] text-[#D4A95A] mb-5">Our Studio</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] max-w-3xl mx-auto">
                                {lang === "es" ? "Donde Aprendes" : "Where You Learn"}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1400px] mx-auto">
                            <div className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img
                                    src={IMG.trainingStudio}
                                    alt="Permanent makeup training apprenticeship studio Las Vegas - apprentices practicing at Beauty Ink LV"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img
                                    src={IMG.trainingMannequins}
                                    alt="PMU training students practicing on mannequins at Beauty Ink LV Las Vegas apprenticeship program"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img
                                    src={IMG.trainingWendy}
                                    alt="Wendy Chester teaching permanent makeup training course Las Vegas"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img
                                    src={IMG.trainingImage3}
                                    alt="PMU apprentice practicing eyebrow technique on mannequin at Beauty Ink LV Las Vegas"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img
                                    src={IMG.trainingWendy1}
                                    alt="Wendy Chester permanent makeup training demonstration Las Vegas"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img
                                    src={IMG.trainingWendy2}
                                    alt="PMU apprentice learning from Wendy Chester at Beauty Ink LV Las Vegas"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                        {/* Training Video */}
                        <div className="mt-8 max-w-[800px] mx-auto">
                            <div className="overflow-hidden aspect-video bg-[#F5F2E4]">
                                <video
                                    src={IMG.videoWendyTraining3}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Earnings */}
                    <div
                        data-testid="training-earnings"
                        className="bg-[#3D3D24] text-[#FFFFF3] p-10 md:p-16 mb-20 max-w-[1100px] mx-auto relative overflow-hidden bink-grain"
                    >
                        <div className="relative z-10 grid lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-start">
                            <div className="w-20 h-20 bg-[#D4A95A] text-[#3D3D24] flex items-center justify-center shrink-0">
                                <DollarSign size={36} strokeWidth={1.5} />
                            </div>
                            <div>
                                <div className="font-label text-[11px] text-[#D4A95A] mb-4">Income Potential</div>
                                <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] mb-6">
                                    {r.payTitle}
                                </h2>
                                <p className="text-base md:text-lg leading-relaxed opacity-90 mb-6">
                                    {r.payBody}
                                </p>
                                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                                    <div className="border-l-2 border-[#D4A95A] pl-4">
                                        <div className="font-serif text-2xl md:text-3xl">$500–$1,200</div>
                                        <div className="font-label text-[10px] opacity-70 mt-1">PER SERVICE</div>
                                    </div>
                                    <div className="border-l-2 border-[#D4A95A] pl-4">
                                        <div className="font-serif text-2xl md:text-3xl">8–15</div>
                                        <div className="font-label text-[10px] opacity-70 mt-1">SERVICES / WEEK</div>
                                    </div>
                                    <div className="border-l-2 border-[#D4A95A] pl-4">
                                        <div className="font-serif text-2xl md:text-3xl">$80K–$200K+</div>
                                        <div className="font-label text-[10px] opacity-70 mt-1">ANNUAL RANGE</div>
                                    </div>
                                </div>
                                <p className="text-[11px] opacity-60 italic leading-relaxed max-w-2xl">
                                    {r.payDisclaimer}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14 lg:gap-20 max-w-[1200px] mx-auto">
                        <div>
                            <div className="font-label text-[11px] text-[#D4A95A] mb-5">Apply</div>
                            <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] mb-6">
                                {r.formTitle}
                            </h2>
                            <p className="text-[#3D3D24]/80 leading-relaxed">{r.formNote}</p>
                        </div>

                        <form
                            data-testid="training-form"
                            onSubmit={onSubmit}
                            className="space-y-5 bg-[#F5F2E4] p-8 md:p-10"
                        >
                            {status.success && (
                                <div data-testid="training-success" className="bg-[#3D3D24] text-[#FFFFF3] p-4 flex items-center gap-3 text-sm">
                                    <Check size={18} className="text-[#D4A95A]" />
                                    {t.common.thankYou}
                                </div>
                            )}
                            {status.error && (
                                <div data-testid="training-error" className="bg-red-50 border border-red-200 text-red-700 p-4 text-sm">
                                    {status.error}
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field testid="training-name" label="Name" name="name" value={form.name} onChange={onChange} required />
                                <Field testid="training-email" label="Email" type="email" name="email" value={form.email} onChange={onChange} required />
                                <Field testid="training-phone" label="Phone" name="phone" value={form.phone} onChange={onChange} required />
                                <Field testid="training-instagram" label="Instagram" name="instagram" value={form.instagram} onChange={onChange} />
                                <Field testid="training-portfolio" label="Portfolio Link" name="portfolio_link" value={form.portfolio_link} onChange={onChange} />
                                <Field testid="training-start" label="When can you start?" name="start_timeframe" value={form.start_timeframe} onChange={onChange} />
                            </div>

                            <div>
                                <label className="font-label text-[10px] text-[#3D3D24]/70 mb-2 block">
                                    Your Background<span className="text-[#D4A95A] ml-1">*</span>
                                </label>
                                <textarea
                                    data-testid="training-background"
                                    name="background"
                                    value={form.background}
                                    onChange={onChange}
                                    required
                                    rows={4}
                                    placeholder="License, current esthetic/PMU/tattoo experience, training already completed…"
                                    className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none resize-none"
                                />
                            </div>

                            <div>
                                <label className="font-label text-[10px] text-[#3D3D24]/70 mb-2 block">Goals</label>
                                <textarea
                                    data-testid="training-goals"
                                    name="goals"
                                    value={form.goals}
                                    onChange={onChange}
                                    rows={3}
                                    placeholder="What are you hoping to gain from the apprenticeship?"
                                    className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none resize-none"
                                />
                            </div>

                            <button
                                data-testid="training-submit"
                                type="submit"
                                disabled={status.submitting}
                                className="w-full bg-[#3D3D24] text-[#FFFFF3] py-4 font-label text-xs hover:bg-[#D4A95A] hover:text-[#3D3D24] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                            >
                                {status.submitting ? <><Loader2 size={14} className="animate-spin" /> {t.common.submitting}</> : t.common.submit}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

const Field = ({ testid, label, name, value, onChange, type = "text", required }) => (
    <div>
        <label className="font-label text-[10px] text-[#3D3D24]/70 mb-2 block">
            {label}{required && <span className="text-[#D4A95A] ml-1">*</span>}
        </label>
        <input
            data-testid={testid}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none"
        />
    </div>
);

export default Training;
